import { ExpoSpeechRecognitionModule } from "@-/com.npmjs.expo-speech-recognition";
import { cn } from "@-/com.npmjs.cn";
import { View } from "@-/com.npmjs.react-native";
import { useEffect, useState } from "react";

function emptyBars(): number[] {
  return Array.from<number>({ length: 48 }).fill(0);
}
const BAR_HEIGHTS = [
  "h-0.5",
  "h-1",
  "h-1.5",
  "h-2",
  "h-2.5",
  "h-3",
  "h-3.5",
  "h-4",
  "h-4.5",
  "h-5",
  "h-5.5",
  "h-6",
];

/** Input history from the recognizer's microphone, including while prompts play. */
export function VoiceStudyWaveform({ enabled }: { enabled: boolean }) {
  const [bars, setBars] = useState(emptyBars);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const scheduleReset = () => {
      clearTimeout(timeout);
      // Pausing removes the listeners and cancels this reset, preserving the history.
      // Unexpected capture interruptions still clear stale activity while Voice is running.
      timeout = setTimeout(setBars, 800, emptyBars);
    };
    const subscriptions = [
      ExpoSpeechRecognitionModule.addListener("volumechange", ({ value }) => {
        // Values below zero are inaudible; ten is the recognizer's maximum level.
        const level = Number.isFinite(value) ? (Math.min(10, Math.max(0, value)) / 10) ** 1.8 : 0;
        setBars((history) => [...history.slice(1), level]);
        scheduleReset();
      }),
      ExpoSpeechRecognitionModule.addListener("audioend", scheduleReset),
      ExpoSpeechRecognitionModule.addListener("end", scheduleReset),
      ExpoSpeechRecognitionModule.addListener("error", scheduleReset),
    ];
    return () => {
      clearTimeout(timeout);
      for (const subscription of subscriptions) {
        subscription.remove();
      }
    };
  }, [enabled]);

  return (
    <View
      accessibilityLabel={enabled ? "Microphone input waveform" : "Microphone paused"}
      accessible
      className="h-6 flex-row items-center gap-0.5 px-2"
      testID="voice-study-waveform"
    >
      {bars.map((level, index) => (
        <View
          className={cn(
            "flex-1 rounded-full",
            enabled ? "bg-primary/70" : "bg-muted-foreground/30",
            BAR_HEIGHTS[Math.round(level * (BAR_HEIGHTS.length - 1))],
          )}
          // eslint-disable-next-line react/no-array-index-key -- Fixed positions in a rolling history.
          key={index}
        />
      ))}
    </View>
  );
}
